/**
 * Base API Client for Cloudflare API Gateway
 * Feature: 007-cloudflare-migration, 008-better-auth-integration
 *
 * Thin fetch wrapper with Better Auth session token authentication.
 * Constitution I (KISS): fetch is native and sufficient for REST.
 */

import {
  getAccessToken,
  hasValidSession,
  checkSessionNeedsRefresh,
  getSession,
  clearStoredToken,
} from './auth'
import {
  ApiError,
  parseApiError,
  handleNetworkError,
  reportErrorToSentry,
} from './errors'
import type { ApiErrorResponse } from './types'

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

if (!API_BASE_URL) {
  throw new Error('VITE_API_BASE_URL environment variable is required')
}

const DEFAULT_TIMEOUT = 30000 // 30 seconds

// ============================================================================
// Types
// ============================================================================

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Request body (will be JSON stringified) */
  body?: unknown
  /** Query parameters */
  params?: Record<string, string | number | boolean | undefined>
  /** Request timeout in milliseconds */
  timeout?: number
  /** Skip authentication header */
  noAuth?: boolean
}

// ============================================================================
// Session Token Management (Better Auth)
// ============================================================================

/**
 * Re-export getAccessToken from auth service.
 * Used for Authorization header in API requests.
 */
export { getAccessToken }

/**
 * Check if current session is valid (quick check without API call).
 * Uses cached session data for fast validation.
 */
export function isSessionValid(): boolean {
  return hasValidSession()
}

/**
 * Check if session needs refresh and perform lazy refresh if needed.
 * This is called before API requests to ensure token validity.
 */
export async function ensureValidSession(): Promise<boolean> {
  if (!hasValidSession()) {
    return false
  }

  // Lazy refresh: only refresh if session is about to expire
  if (checkSessionNeedsRefresh()) {
    try {
      const session = await getSession()
      return session !== null
    } catch {
      return false
    }
  }

  return true
}

// ============================================================================
// Request Building
// ============================================================================

/**
 * Build full URL with query parameters
 */
function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  // Ensure base URL ends with / and endpoint doesn't start with /
  // This preserves the /v1 path in the base URL when using new URL()
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
  
  const url = new URL(cleanEndpoint, baseUrl)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.set(key, String(value))
      }
    })
  }

  return url.toString()
}

/**
 * Build request headers with JWT auth
 */
async function buildHeaders(
  options: RequestOptions
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  // Add authorization header unless explicitly skipped
  if (!options.noAuth) {
    const token = await getAccessToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  // Merge with any custom headers
  if (options.headers) {
    const customHeaders = options.headers as Record<string, string>
    Object.assign(headers, customHeaders)
  }

  return headers
}

// ============================================================================
// API Client
// ============================================================================

/**
 * Make an API request to Cloudflare API Gateway
 *
 * @param endpoint - API endpoint (relative to base URL)
 * @param options - Request options
 * @returns Parsed JSON response
 * @throws ApiError on failure
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, params, timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options

  const url = buildUrl(endpoint, params)
  const headers = await buildHeaders(options)
  console.log('DEBUG apiRequest:', { endpoint, method: options.method, headers, hasAuth: !!headers['Authorization'] })
  const method = options.method || 'GET'

  // Create abort controller for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const fetchConfig = {
      ...fetchOptions,
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    }
    console.log('DEBUG fetch config:', { url, fetchConfig, headersInConfig: fetchConfig.headers })
    const response = await fetch(url, fetchConfig)

    clearTimeout(timeoutId)

    // Handle non-OK responses
    if (!response.ok) {
      let errorBody: ApiErrorResponse | undefined
      try {
        errorBody = await response.json()
      } catch {
        // Response body may not be JSON
      }

      const error = parseApiError(response, errorBody)

      // Report to Sentry
      reportErrorToSentry(error, {
        endpoint,
        method,
        params: params as Record<string, unknown>,
      })

      throw error
    }

    // Handle empty responses
    if (response.status === 204) {
      return undefined as T
    }

    // Parse JSON response
    return await response.json()
  } catch (error) {
    clearTimeout(timeoutId)

    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error
    }

    // Handle network/fetch errors
    const apiError = handleNetworkError(error)

    // Report to Sentry
    reportErrorToSentry(apiError, {
      endpoint,
      method,
      params: params as Record<string, unknown>,
    })

    throw apiError
  }
}

// ============================================================================
// Convenience Methods
// ============================================================================

/**
 * GET request
 */
export function get<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>,
  options?: Omit<RequestOptions, 'method' | 'body' | 'params'>
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'GET', params })
}

/**
 * POST request
 */
export function post<T>(
  endpoint: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'POST', body })
}

/**
 * PUT request
 */
export function put<T>(
  endpoint: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'PUT', body })
}

/**
 * PATCH request
 */
export function patch<T>(
  endpoint: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'PATCH', body })
}

/**
 * DELETE request
 */
export function del<T>(
  endpoint: string,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' })
}

// ============================================================================
// Session Validation
// ============================================================================

/**
 * Validate current session with Better Auth API.
 * Used for session validation on protected routes.
 */
export async function validateSession(): Promise<boolean> {
  try {
    // Quick check using cached session
    if (!hasValidSession()) {
      return false
    }

    // Validate with API if session is near expiry
    if (checkSessionNeedsRefresh()) {
      const session = await getSession()
      return session !== null
    }

    return true
  } catch (error) {
    if (error instanceof ApiError && error.isAuthError) {
      return false
    }
    throw error
  }
}

/**
 * Handle session expiry.
 * Clears local session and returns redirect path.
 */
export function handleSessionExpiry(): string {
  clearStoredToken()
  return '/admin/login'
}
